import { useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const categories = ['All', 'Web Application', 'Mobile Application', 'Custom Software', 'Web Platform'];

const projects = [
  {
    title: 'FinTrack Pro', category: 'Web Application',
    description: 'A comprehensive financial management platform with real-time analytics, portfolio tracking, automated reporting, and multi-currency support for enterprise clients across 12 countries.',
    tech: ['React', 'Node.js', 'PostgreSQL', 'AWS', 'WebSocket'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80',
    result: '40% increase in operational efficiency',
  },
  {
    title: 'MediConnect', category: 'Mobile Application',
    description: 'Cross-platform healthcare app enabling virtual consultations, appointment scheduling, secure medical record access, and prescription management with HIPAA-compliant architecture.',
    tech: ['React Native', 'Firebase', 'Node.js', 'HIPAA', 'WebRTC'],
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&auto=format&fit=crop&q=80',
    result: '300% increase in patient engagement',
  },
  {
    title: 'LogiFlow', category: 'Custom Software',
    description: 'End-to-end logistics management system with real-time GPS tracking, AI-powered route optimization, automated dispatch workflows, and comprehensive analytics dashboard.',
    tech: ['Python', 'Django', 'Redis', 'Docker', 'ML'],
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&auto=format&fit=crop&q=80',
    result: '25% reduction in delivery times',
  },
  {
    title: 'EduSphere', category: 'Web Platform',
    description: 'Interactive e-learning platform featuring live video sessions, AI-powered assessments, adaptive learning paths, and a marketplace for course creators.',
    tech: ['Next.js', 'GraphQL', 'MongoDB', 'WebRTC', 'AI'],
    image: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&auto=format&fit=crop&q=80',
    result: '50,000+ active learners',
  },
  {
    title: 'ShopNova', category: 'Web Application',
    description: 'Headless e-commerce platform with AI-powered product recommendations, real-time inventory management, and multi-channel selling capabilities.',
    tech: ['Next.js', 'Stripe', 'Algolia', 'AWS', 'GraphQL'],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&auto=format&fit=crop&q=80',
    result: '35% boost in conversion rate',
  },
  {
    title: 'FitPulse', category: 'Mobile Application',
    description: 'Fitness tracking app with AI-powered workout plans, real-time heart rate monitoring via wearable integration, nutrition tracking, and social challenges.',
    tech: ['Flutter', 'Firebase', 'HealthKit', 'TensorFlow Lite'],
    image: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=800&auto=format&fit=crop&q=80',
    result: '1M+ downloads in 6 months',
  },
  {
    title: 'DataForge', category: 'Custom Software',
    description: 'Enterprise data pipeline platform with ETL automation, real-time data streaming, visual query builder, and customizable reporting dashboards.',
    tech: ['Python', 'Apache Kafka', 'Spark', 'React', 'Docker'],
    image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&auto=format&fit=crop&q=80',
    result: '10x faster data processing',
  },
  {
    title: 'TravelMate', category: 'Web Platform',
    description: 'AI-powered travel planning platform with personalized itinerary generation, real-time price tracking, group trip coordination, and local experience marketplace.',
    tech: ['Next.js', 'OpenAI', 'Stripe', 'PostgreSQL', 'Redis'],
    image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&auto=format&fit=crop&q=80',
    result: '200K+ trips planned',
  },
];

export default function PortfolioPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const filtered = activeFilter === 'All' ? projects : projects.filter((p) => p.category === activeFilter);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} data-testid="portfolio-page">
      {/* Hero */}
      <section ref={heroRef} className="relative min-h-[60vh] flex items-center overflow-hidden pt-20">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&auto=format&fit=crop&q=80" alt="Portfolio" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-slate-950" />
        </motion.div>
        <motion.div style={{ opacity: heroOpacity }} className="relative max-w-7xl mx-auto px-6 md:px-12 py-20">
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">Portfolio</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight text-white mb-6">
            Our <span className="glow-text">Work</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed">
            A curated showcase of projects that demonstrate our commitment to quality, innovation, and measurable results.
          </motion.p>
        </motion.div>
      </section>

      {/* Filter + Projects */}
      <section className="relative py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Filter Tabs */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex flex-wrap gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`portfolio-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setActiveFilter(cat)}
                className={`px-4 py-2 text-sm rounded-full border transition-all duration-300 ${
                  activeFilter === cat
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                }`}
              >
                {cat}
              </button>
            ))}
          </motion.div>

          {/* Projects Grid */}
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filtered.map((project, index) => (
              <ProjectCard key={project.title} project={project} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-[#0a0f1e]">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-white mb-6">Have a Project in Mind?</h2>
          <p className="text-base text-slate-400 mb-8">Let's create something remarkable together.</p>
          <Button data-testid="portfolio-page-cta" onClick={() => navigate('/contact')} className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 h-12 text-base font-semibold hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] group">
            Start a Conversation <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </motion.div>
  );
}

function ProjectCard({ project, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      layout
      whileHover={{ y: -6 }}
      data-testid={`portfolio-page-card-${index}`}
      className="group rounded-2xl overflow-hidden border border-white/5 bg-slate-900/30 hover:border-blue-500/20 transition-all duration-500"
    >
      <div className="relative h-60 overflow-hidden">
        <motion.img whileHover={{ scale: 1.06 }} transition={{ duration: 0.6 }} src={project.image} alt={project.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
          <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center"><ExternalLink className="w-4 h-4 text-white" /></div>
        </div>
        {project.result && (
          <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-blue-500/20 backdrop-blur-sm border border-blue-500/20">
            <span className="text-xs font-medium text-blue-300">{project.result}</span>
          </div>
        )}
      </div>
      <div className="p-6">
        <span className="text-xs font-medium text-blue-400 tracking-wide uppercase">{project.category}</span>
        <h3 className="font-heading font-semibold text-xl text-white mt-2 mb-3">{project.title}</h3>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">{project.description}</p>
        <div className="flex flex-wrap gap-2">
          {project.tech.map((t) => (
            <span key={t} className="text-xs px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-300 border border-blue-500/10">{t}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
